import React from 'react';
import {Button, Checkbox, FormGroup, HTMLSelect, InputGroup, TextArea} from "@blueprintjs/core";
import {PANEL_OPTIONS, CONDITIONS, CONDITIONS_BY_PANEL} from "components/Report/index";

/** ShowChildren - show/hide children based on "when".  Nifty replacement for ? and && conditional code. */
function ShowChildren({ when, fallback=null, children }) { return when ? children : fallback; }

/**
 * Remove duplicates from a given array of string values
 * @param arr - array of string values
 * @returns {string[]} - arr with any duplicates removed
 */
const removeDuplicates = arr => Array.from(new Set( Array.isArray(arr) ? arr : [] ));

const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

/**
 * Component for Main section (tab) on Report-Form page
 * @param props
 *     values - initial values for fields.  Final values of fields upon completion.
 *     setValues - hook function to update values
 *     update - function to update db with form values.  Include report name to issue report.
 */
const MainSection = ({values, setValues, update}) => {

  /** User has clicked (checked or unchecked) a checkbox field.  Update local state and db. */
  const onCheckboxValueChange = (newValues) => {
    setValues(newValues);
    update(undefined, newValues);
  }

  /**
   * When change-handler called for checkbox field that's part of a string array, toggle its value within array.
   * @param checkboxValue - value that is added/removed from field array based on checkbox truth.  E.g. 'M'.
   * @param checked - boolean value of checkbox.  If true, add checkboxValue to field's array.  If false, remove it.
   * @param fieldName - index (key) of string array field within values object.  e.g. 'testTypes'
   */
  const onChangeElement = (checkboxValue, checked, fieldName) => {
    const newArray = removeDuplicates(values[fieldName].slice()); // copy string array and remove dups
    const valueIndex = newArray.indexOf(checkboxValue);

    if (checked) {
      if (!newArray.includes(checkboxValue)) {
        newArray.push(checkboxValue);
      }
    } else {
      if (newArray.includes(checkboxValue)) {
        newArray.splice(valueIndex, 1);
      }
    }

    const updatedValues = {...values, [fieldName]: newArray};
    setValues(updatedValues);
    update(undefined, updatedValues);
  }

  const {
    analysisVersion,
    reportVersion,
    // caseId,
    // patientName,
    // partnerName,
    // isAmended,
    // clinicianName,
    panel,
    selectedPanel,
    reportInfo,
    testTypes,
    blinded,
    show_db,
    showPTableForAneuploid,
    hidePartner,
    hideSexInKaryotype,
  } = values;

  const {
    patientPartnerNameDob = false,
    embryoNumbers = false,
    embryoGradeBiopsyDate = false,
    samplesReceivedDate = false,
    referringClinician = false,
    karyotypes = false,
    formatting = false,
    isAmended = false,
    isRevised = false,
    isPreliminary = false,
    isFinal = false
  } = values.reportChecks || {};

  const {
    patientDemographics = false,
    clinicReferringPhysician = false,
    sampleInformation = false,
    finalSampleResult = false
  } = values.m2ReportChecks || {};

  /**
   * When user clicks "Issue Report" button, build report's name and call issueReport.
   * @param event - event object from button click.
   */
  const handleIssueReport = async (event) => {
    event.preventDefault();
    let reportSuffixes = "";
    if (isAmended) reportSuffixes += '_amended';
    if (isRevised) reportSuffixes += '_revised';
    if (isPreliminary) reportSuffixes += '_preliminary';
    if (isFinal) reportSuffixes += '_final';
    update(reportSuffixes ? reportSuffixes : 'none');  // update db with form values, then issue report
  }

  return (
    <div>
      <FormGroup
        label="Analysis Version"
        labelFor="analysisVersion"
        inline
      >
        <InputGroup
          id="analysisVersion"
          value={analysisVersion || ''}
          onChange={e => setValues(v => ({...v, analysisVersion: e.target.value}))}
        />
      </FormGroup>

      <FormGroup
        label="Report Version"
        labelFor="reportVersion"
        inline
      >
        <InputGroup
          id="reportVersion"
          value={reportVersion || ''}
          onChange={e => setValues(v => ({...v, reportVersion: e.target.value}))}
        />
      </FormGroup>

      <div style={{
        backgroundColor: '#d8dbff',
        borderRadius: '8px'
      }}>
        <div style={{padding: '10px 0 20px 10px'}}>Review Checklist:</div>

        <ShowChildren when={Array.isArray(testTypes) && (testTypes.includes('A') || testTypes.includes('A+'))}>
          <table style={{margin: '0 0 10px 10px'}}>
            <tbody>
              <tr>
                <td>
                    <Checkbox
                      checked={!!patientPartnerNameDob}
                      label="Patient/Partner Names and DOBs"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), patientPartnerNameDob: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                <td>
                    <Checkbox
                      checked={!!embryoNumbers}
                      label="Embryo Numbers"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), embryoNumbers: e.target.checked}})}
                      inline
                    />
                </td>
              </tr>
              <tr>
                <td>
                    <Checkbox
                      checked={!!embryoGradeBiopsyDate}
                      label="Embryo Grade/Biopsy Date (if incl.)"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), embryoGradeBiopsyDate: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                <td>
                    <Checkbox
                      checked={!!samplesReceivedDate}
                      label="Samples Received Date"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), samplesReceivedDate: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                {patientPartnerNameDob && embryoNumbers && embryoGradeBiopsyDate &&
                  samplesReceivedDate && referringClinician && karyotypes && formatting && (
                    <td style={{width: '40%', textAlign: 'right'}}>
                      <Button intent="primary" icon="document-share" style={{marginRight: '20px'}}
                              onClick={(e) => {handleIssueReport(e)}}>
                        Issue Report
                      </Button>
                    </td>
                  )}
              </tr>
              <tr>
                <td>
                    <Checkbox
                      checked={!!referringClinician}
                      label="Referring Clinician"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), referringClinician: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                <td>
                    <Checkbox
                      checked={!!karyotypes}
                      label="Karyotypes"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), karyotypes: e.target.checked}})}
                      inline
                    />
                </td>
              </tr>
              <tr>
                <td>
                    <Checkbox
                      checked={!!formatting}
                      label="No Formatting Issues"
                      onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), formatting: e.target.checked}})}
                      inline
                    />
                </td>
              </tr>
            </tbody>
          </table>
        </ShowChildren>
        <ShowChildren when={Array.isArray(testTypes) && !testTypes.includes('A') && !testTypes.includes('A+')}>
          <table style={{margin: '0 0 10px 10px'}}>
            <tbody>
              <tr>
                <td>
                    <Checkbox
                      checked={!!patientDemographics}
                      label="Patient Demographics"
                      onChange={e => onCheckboxValueChange({...values, m2ReportChecks: {...(values.m2ReportChecks || {}), patientDemographics: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                <td>
                    <Checkbox
                      checked={!!clinicReferringPhysician}
                      label="Clinic/Referring Physician"
                      onChange={e => onCheckboxValueChange({...values, m2ReportChecks: {...(values.m2ReportChecks || {}), clinicReferringPhysician: e.target.checked}})}
                      inline
                    />
                </td>
                {patientDemographics && clinicReferringPhysician && sampleInformation && finalSampleResult && (
                    <td style={{width: '40%', textAlign: 'right'}}>
                      <Button intent="primary" icon="document-share" style={{marginRight: '20px'}}
                              onClick={(e) => {handleIssueReport(e)}}>
                        Issue Report
                      </Button>
                    </td>
                  )}
              </tr>
              <tr>
                <td>
                    <Checkbox
                      checked={!!sampleInformation}
                      label="Sample Information"
                      onChange={e => onCheckboxValueChange({...values, m2ReportChecks: {...(values.m2ReportChecks || {}), sampleInformation: e.target.checked}})}
                      inline
                    />
                </td>
                <td>&nbsp;</td>
                <td>
                    <Checkbox
                      checked={!!finalSampleResult}
                      label="Final Sample Result"
                      onChange={e => onCheckboxValueChange({...values, m2ReportChecks: {...(values.m2ReportChecks || {}), finalSampleResult: e.target.checked}})}
                      inline
                    />
                </td>
              </tr>
            </tbody>
          </table>
        </ShowChildren>
      </div>

      <div style={{marginTop: '15px', marginBottom: '5px', padding: '10px 0 10px 10px'}}>
        <span style={{marginRight: '50px'}}>Report is:</span>
        <Checkbox
          checked={!!isAmended}
          label="Amended"
          onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), isAmended: e.target.checked}})}
          inline
        />

        <Checkbox
          checked={!!isRevised}
          label="Revised"
          onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), isRevised: e.target.checked}})}
          inline
        />

        <Checkbox
          checked={!!isPreliminary}
          label="Preliminary"
          onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), isPreliminary: e.target.checked}})}
          inline
        />

        <Checkbox
          checked={!!isFinal}
          label="Final"
          onChange={e => onCheckboxValueChange({...values, reportChecks: {...(values.reportChecks || {}), isFinal: e.target.checked}})}
          inline
        />
      </div>

      <FormGroup
        label={capitalize(panel) + ' panel'}
        labelFor="panel"
        inline
      />

      <FormGroup
        label="PGT-P Panel"
        labelFor="selectedPanel"
        inline
      >
        <HTMLSelect
          value={selectedPanel || ''}
          onChange={e => setValues({...values, selectedPanel: e.currentTarget.value, diseases: CONDITIONS_BY_PANEL[e.currentTarget.value]})}
        >
          <option value="">Select...</option>
          {
            Object.keys(PANEL_OPTIONS).map((key) => {
              return (
                <option key={key} value={key}>{PANEL_OPTIONS[key]}</option>
              )
            })
          }
        </HTMLSelect>
      </FormGroup>

      {panel === 'custom' && (
        <FormGroup
          label="Сonditions"
          inline
        >
          {
            Object.keys(CONDITIONS).map((key) => {
              return (
                <Checkbox key={key}
                  checked={values.diseases && values.diseases.includes(key)}
                  label={CONDITIONS[key]}
                  onChange={(e) => onChangeElement(key, e.target.checked, 'diseases')}
                  disabled={!CONDITIONS_BY_PANEL[selectedPanel].includes(key)}
                  inline
                />
              )
            })
          }
        </FormGroup>
      )}

      <FormGroup
        label="Info"
        labelFor="reportInfo"
        style={{minHeight: 120}}
      >
        <TextArea
          growVertically={true}
          fill={true}
          id="reportInfo"
          value={reportInfo || ''}
          onChange={e => setValues(v => ({...v, reportInfo: e.target.value}))}
          style={{minHeight: 120}}
        />
      </FormGroup>

      <FormGroup
        label="Test Types"
        labelFor="testTypes"
        inline
      >
        <Checkbox
          checked={Array.isArray(testTypes) && testTypes.includes('A')}
          label="A"
          onChange={(e) => onChangeElement('A', e.target.checked, 'testTypes')}
          disabled
          inline
        />
        <Checkbox
          checked={Array.isArray(testTypes) && testTypes.includes('A+')}
          label="A+"
          onChange={(e) => onChangeElement('A+', e.target.checked, 'testTypes')}
          disabled
          inline
        />
        <Checkbox
          checked={Array.isArray(testTypes) && testTypes.includes('M')}
          label="M"
          onChange={(e) => onChangeElement('M', e.target.checked, 'testTypes')}
          disabled
          inline
        />
        <Checkbox
          checked={Array.isArray(testTypes) && testTypes.includes('P')}
          label="P"
          onChange={(e) => onChangeElement('P', e.target.checked, 'testTypes')}
          disabled
          inline
        />
        <Checkbox
          checked={Array.isArray(testTypes) && testTypes.includes('SR')}
          label="SR"
          onChange={(e) => onChangeElement('SR', e.target.checked, 'testTypes')}
          disabled
          inline
        />
      </FormGroup>

      <FormGroup
        label="Blinded"
        labelFor="blinded"
        inline
      >
        <Checkbox
          checked={!!blinded}
          label=""
          onChange={e => setValues(v => ({...v, blinded: e.target.checked}))}
          inline
        />
      </FormGroup>

      <FormGroup
        label="Show DoB if blinded"
        labelFor="show_db"
        inline
      >
        <Checkbox
          checked={!!show_db}
          label=""
          onChange={e => setValues(v => ({...v, show_db: e.target.checked}))}
          inline
        />
      </FormGroup>

      <FormGroup
        label="Show table PGT-P when aneuploid"
        labelFor="showPTableForAneuploid"
        inline
      >
        <Checkbox
          checked={!!showPTableForAneuploid}
          label=""
          onChange={e => setValues(v => ({...v, showPTableForAneuploid: e.target.checked}))}
          inline
        />
      </FormGroup>

      <FormGroup
        label="Hide Partner"
        labelFor="hidePartner"
        inline
      >
        <Checkbox
          checked={!!hidePartner}
          label=""
          onChange={e => setValues(v => ({...v, hidePartner: e.target.checked}))}
          inline
        />
      </FormGroup>

      <FormGroup
        label="Hide sex in karyotype"
        labelFor="hideSexInKaryotype"
        inline
      >
        <Checkbox
          checked={!!hideSexInKaryotype}
          label=""
          onChange={e => setValues(v => ({...v, hideSexInKaryotype: e.target.checked}))}
          inline
        />
      </FormGroup>
    </div>
  )
}

export default MainSection;
